// Copyright 2017 Sorama B.V.

import { autoinject, computedFrom } from "aurelia-framework";
import { ActivatedLicenseDTO, LicenseDTO, SellableLicense } from "datatypes/SoramaWebApi";
import { IAuthentication, IAuthenticationHandler } from "libraries/Authentication/IAuthentication";
import { IIndexableObject } from "libraries/IndexedDb/IIndexableObject";
import { SoramaPortalCacheDb } from "libraries/IndexedDb/SoramaPortalCacheDb";
import { LicenseHolderRepository } from "repositories/LicenseHolderRepository";
import { LicenseRepository } from "repositories/LicenseRepository";
import { ILicenseService } from "services/License/ILicenseService";
import { INotificationService, NotificationUrgency } from "services/Notification/INotificationService";
import { IServerStatusService, ServerStatus } from "services/Server/IServerStatusService";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";

class StoredMembershipLicense implements IIndexableObject {
    public id: string;
    public userId: string;
    public membershipLicense: ActivatedLicenseDTO;

    constructor(license: ActivatedLicenseDTO, userId: string, id?: string) {
        this.membershipLicense = license;
        this.id = id ? id : userId;
        this.userId = userId;
    }
}

@autoinject
export class LicenseService implements ILicenseService, IAuthenticationHandler {
    private static moduleSettingsGroupKey = "LicenseService";

    //#region Properties
    private _activeMembershipLicense: ActivatedLicenseDTO;
    private _activeMembershipLicenseLoaded: boolean;
    private _lastCheckedDate: Date;
    private _activeMembershiplicensePromise: Promise<LicenseDTO>;

    public sellableFeatureLicenses: SellableLicense[];

    @computedFrom("_activeMembershipLicenseLoaded")
    public get activeMembershipLicenseLoaded(): boolean {
        return this._activeMembershipLicenseLoaded;
    }

    @computedFrom("_activeMembershipLicense")
    public get activeMembershipLicense(): ActivatedLicenseDTO {
        return this._activeMembershipLicense;
    }
    //#endregion

    //#region Constructor
    constructor(
        private readonly soramaPortalCacheDb: SoramaPortalCacheDb,
        private readonly licenseHolderRepository: LicenseHolderRepository,
        private readonly serverStatusService: IServerStatusService,
        private readonly notificationService: INotificationService,
        private readonly userSettingsService: IUserSettingsService,
        private readonly authentication: IAuthentication,
        private readonly licenseRepository: LicenseRepository
    ) {
        this._activeMembershipLicenseLoaded = false;
        this.authentication.addAuthenticationHandler(this);

        this.userSettingsService.bind(LicenseService.moduleSettingsGroupKey, "lastCheckedDate", this);
    }
    //#endregion

    private membershipLicenseCheck(license: ActivatedLicenseDTO): void {
        if (!license) {
            this.notificationService.registerNotification(
                "Membership",
                "There is no valid Sorama Membership. Contact Sorama to get a Sorama Membership.",
                NotificationUrgency.Warning
            );

            return;
        }

        const currentDate = Date.now(); // Number of milliseconds elapsed since January 1, 1970 00:00:00 UTC
        const soonDate = currentDate + 60 * 24 * 60 * 60 * 1000; // Notify the user approx. 2 month before it expires

        // Check if we need to check the license again this day
        if (this._lastCheckedDate) {
            // Make sure that it is a date object
            const date = new Date(this._lastCheckedDate);

            if (date.getTime() >= currentDate - 24 * 60 * 60 * 1000) {
                return;
            }
        }

        this._lastCheckedDate = new Date(Date.now());

        // Check if the current license is valid
        if (license.DateEnd.getTime() <= soonDate) {
            // If not, check if there is a new license ready
            this.soramaPortalCacheDb
                .getAll<StoredMembershipLicense>(SoramaPortalCacheDb.LicenseStoreName)
                .then((storedLicenses) => {
                    if (
                        storedLicenses.filter(
                            (x) =>
                                x.membershipLicense.DateStart.getTime() <= soonDate &&
                                x.membershipLicense.DateEnd.getTime() > soonDate &&
                                !x.membershipLicense.IsCanceled
                        ).length == 0
                    ) {
                        this.notificationService.registerNotification(
                            "Membership",
                            "Your membership will expire soon. Contact Sorama to renew your membership.",
                            NotificationUrgency.Warning
                        );
                    }
                });
        }
    }

    //#region IAuthenticationHandler members
    public async onSignIn(userId: string): Promise<void> {
        this.reloadSellableFeatureLicenses();

        this._activeMembershiplicensePromise = this.retrieveMembershipLicense(userId);
    }

    private async retrieveMembershipLicense(userId: string): Promise<LicenseDTO> {
        if (this.serverStatusService.status === ServerStatus.Online) {
            return this.refreshMembershipLicenses(userId);
        } else {
            // Grab existing license when offline
            return this.retrieveExistingMembershipLicenses(userId);
        }
    }

    private async retrieveExistingMembershipLicenses(userId: string): Promise<LicenseDTO> {
        const storedMembershipLicenses = await this.soramaPortalCacheDb.getAll<StoredMembershipLicense>(
            SoramaPortalCacheDb.LicenseStoreName
        );

        const now = Date.now(); // elapsed time in ms since in UTC

        const sortedMembershipLicenses = storedMembershipLicenses
            .filter(
                (x) =>
                    x.membershipLicense.DateStart.getTime() <= now &&
                    x.membershipLicense.DateEnd.getTime() > now &&
                    !x.membershipLicense.IsCanceled
            )
            .sort((x) => -x.membershipLicense.LicenseKind.Properties.CreditsBufferCap);

        if (sortedMembershipLicenses.length > 0)
            this._activeMembershipLicense = sortedMembershipLicenses[0].membershipLicense;

        this.membershipLicenseCheck(this._activeMembershipLicense);
        this._activeMembershipLicenseLoaded = true;

        return this._activeMembershipLicense;
    }

    private async refreshMembershipLicenses(userId: string): Promise<LicenseDTO> {
        const membershipLicenses = await this.licenseHolderRepository
            .getActivatedMembershipLicensesForLicenseHolder(this.authentication.businessUnitId)
            .readToEnd();

        const now = Date.now(); // elapsed time in ms since in UTC

        // Find active licence
        const sortedValidMembershipLicenses = membershipLicenses
            .filter((x) => x.DateEnd.getTime() > now && !x.IsCanceled)
            .sort((x) => -x.LicenseKind.Properties.CreditsBufferCap); // sort descending

        // Clear current licenses of this user
        await this.soramaPortalCacheDb.clearObjectStore(SoramaPortalCacheDb.LicenseStoreName);

        const promises: Promise<string>[] = [];
        for (const lic of sortedValidMembershipLicenses) {
            const storedLicense = new StoredMembershipLicense(lic, userId, lic.Id);
            promises.push(
                this.soramaPortalCacheDb.putObject<StoredMembershipLicense>(
                    storedLicense,
                    SoramaPortalCacheDb.LicenseStoreName
                )
            );
        }

        await Promise.all(promises);

        const idx = sortedValidMembershipLicenses.findIndex((x) => x.DateStart.getTime() <= now);
        if (idx >= 0) this._activeMembershipLicense = sortedValidMembershipLicenses[idx];

        this.membershipLicenseCheck(this._activeMembershipLicense);
        this._activeMembershipLicenseLoaded = true;

        return this._activeMembershipLicense;
    }

    public onSignOut(): Promise<void> {
        return Promise.resolve();
    }

    public getActiveMembershipLicense(): Promise<LicenseDTO> {
        return this._activeMembershiplicensePromise ?? Promise.resolve(undefined);
    }

    public async activateFeature(licenseId: string, deviceId: string, hardwareClock: number): Promise<string> {
        const licenseKey = await this.licenseRepository.activateFeature(licenseId, deviceId, hardwareClock);
        return licenseKey;
    }

    public async encryptLicense(licenseId: string, deviceId: string, hardwareClock: number): Promise<string> {
        const encryptedLicense = await this.licenseRepository.encryptLicense(licenseId, deviceId, hardwareClock);
        return encryptedLicense;
    }

    public async getActivatedLicensesForDevice(deviceId: string): Promise<ActivatedLicenseDTO[]> {
        const activeLicenses = await this.licenseHolderRepository.getActivatedLicensesForLicenseHolderAndDevice(
            this.authentication.businessUnitId,
            deviceId
        );
        return activeLicenses;
    }

    public async reloadSellableFeatureLicenses(): Promise<void> {
        this.sellableFeatureLicenses = await this.licenseHolderRepository.getSellableFeatureLicensesForLicenseHolder(
            this.authentication.businessUnitId
        );
    }

    public async restoreFeatureLicenses(deviceId: string, hardwareClock: number): Promise<string[]> {
        const restoredLicenses = await this.licenseHolderRepository.restoreFeatureLicensesForLicenseHolderAndDevice(
            this.authentication.businessUnitId,
            deviceId,
            hardwareClock
        );
        return restoredLicenses;
    }
    //#endregion
}
